import React from 'react';
import ReactDOM from 'react-dom';
import { createStore, compose, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { routerMiddleware } from 'connected-react-router';
import { persistStore, persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import promise from 'redux-promise';
import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import dotenv from 'dotenv';
import createRootReducer from '../reducers';
import Root from '../containers/Root';
import postConfigRoutingMiddleware from '../middlewares/postConfigRoutingMiddleware';
import history from '../utils/history';
import { PERSIST_VERSION, migrations } from '../config/persistMigrations';
import { installSupportSessionRequests } from '../utils/supportSessionRequests';
import sessionStorageEngine from 'redux-persist/lib/storage/session';
import {
  syncSupportSession,
  readSupportSessionId,
} from '../utils/supportSession';
import { composeBeforeSend } from '../config/composeBeforeSend';
import { enrichSentryEventWithBrowserTranslation } from '../config/sentryBrowserTranslation';

dotenv.config();

if (
  process.env.REACT_SENTRY_DSN &&
  process.env.REACT_SENTRY_TRACE_SAMPLE_RATE
) {
  Sentry.init({
    dsn: process.env.REACT_SENTRY_DSN,
    // Without this, uploaded source maps cannot be tied to the build that
    // produced an event and every frame stays minified. Baked in at compile
    // time by DefinePlugin — see config/webpack/sentryRelease.js.
    release: process.env.REACT_SENTRY_RELEASE || undefined,
    replaysSessionSampleRate: parseFloat(
      process.env.REACT_SENTRY_TRACE_SAMPLE_RATE,
    ),
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: parseFloat(process.env.REACT_SENTRY_TRACE_SAMPLE_RATE),
    normalizeDepth: 10,
    // Chained on purpose: Sentry takes exactly one beforeSend and more than
    // one concern wants a hook here. Enrichers first, filters after.
    beforeSend: composeBeforeSend(enrichSentryEventWithBrowserTranslation),
  });
}

const MIXPANEL_TOKENS = {
  prod: 'cfe83be0101fed901abe64885635b1d9',
  stage: '74a7b3d61e4a319eb60eaa307d4e7628',
  dev: '28dc4f492427ee4a9384346c8d53fc2d',
};

// Default to dev if REACT_MIXPANEL_ENV is not set or doesn't match any key in MIXPANEL_TOKENS
const mixpanelToken =
  MIXPANEL_TOKENS[process.env.REACT_MIXPANEL_ENV] || MIXPANEL_TOKENS.dev;

console.log('Mixpanel token:', mixpanelToken);

mixpanel.init(mixpanelToken, {
  debug: true,
  track_pageview: true,
  persistence: 'localStorage',
  ignore_dnt: true,
});

// A support session's state is kept per TAB, not per origin. localStorage is
// shared by every tab on the storefront, so two support tabs would write one
// another's cart and galleries into the same key and each could rehydrate the
// other family's state. sessionStorage is per-tab by construction, and it also
// means nothing a support session built survives the tab closing.
const inSupportSession = readSupportSessionId() !== '';

const persistConfig = {
  key: 'root',
  storage: inSupportSession ? sessionStorageEngine : storage,
  version: PERSIST_VERSION,
  migrate: createMigrate(migrations, { debug: false }),
  blacklist: ['company'],
};

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const middleware = [
  promise,
  routerMiddleware(history),
  thunk,
  postConfigRoutingMiddleware({ history }),
];

// Before the store exists: a different impersonated parent (or none) must not
// rehydrate the previous one's cart, student and gallery state.
syncSupportSession();
installSupportSessionRequests();

// const middleware = [promise];
const persistedReducer = persistReducer(
  persistConfig,
  createRootReducer(history),
);

const store = createStore(
  persistedReducer,
  composeEnhancers(applyMiddleware(...middleware)),
);

document.addEventListener('DOMContentLoaded', () => {
  const rootDiv = document.querySelector('#root');
  const containerDiv = rootDiv.appendChild(document.createElement('div'));

  const configData = document.querySelector('#config-data');
  const data = JSON.parse(configData.getAttribute('data-config'));

  // Set company token in Redux - this is now used for both CSS and business logic
  // Since we've standardized css field to match token in site_settings_by_domain.yml,
  // we can now use a single identifier for everything
  const companyIdentifier = data.token || data.css || data.auth_token;

  if (companyIdentifier) {
    // Set token (preferred identifier)
    store.dispatch({
      type: 'SET_COMPANY_TOKEN',
      payload: companyIdentifier,
    });

    // Also set company.name for backward compatibility during migration
    // This can be removed once all components are updated to use company.token
    store.dispatch({
      type: 'SET_COMPANY_NAME',
      payload: companyIdentifier,
    });
  }

  if (data.non_us_internationalization_enabled !== undefined) {
    store.dispatch({
      type: 'SET_COMPANY_NON_US_INTERNATIONALIZATION_ENABLED',
      payload: !!data.non_us_internationalization_enabled,
    });
  }

  if (data.image_protection_enabled !== undefined) {
    store.dispatch({
      type: 'SET_COMPANY_IMAGE_PROTECTION_ENABLED',
      payload: !!data.image_protection_enabled,
    });
  }

  if (data.image_protection_style !== undefined) {
    store.dispatch({
      type: 'SET_COMPANY_IMAGE_PROTECTION_STYLE',
      payload: data.image_protection_style,
    });
  }

  ReactDOM.render(
    <Root
      {...data}
      history={history}
      store={store}
      persistor={persistStore(store)}
    />,
    containerDiv,
  );
});
